import { useCallback, useEffect, useState } from 'react';
import {
  overdue,
  type OverdueRequest,
  type UncoveredCategory,
} from '../lib/api.ts';
import { StatusPill } from './StatusPill.tsx';
import type { RequestStatus } from '../lib/api.ts';

/**
 * The queue an admin should check daily.
 *
 * Two failure shapes, deliberately on one screen: individual requests
 * past SLA with nobody having responded, and whole categories nobody is
 * assigned to. The second is the one that matters more -- a late ticket
 * is visible, a queue with no supervisor is silent until a client asks
 * why nothing happened for a month.
 */
interface Props {
  /**
   * Opens the ticket. Without it this screen can only tell you
   * something is late, which leaves you hunting for it by hand in
   * Tickets -- and a list you cannot act on stops being read.
   */
  readonly onOpenRequest?: (id: string) => void;
  /** Assigning a supervisor is the fix, and it lives in
      Settings, so the gap links to where it is solved. */
  readonly onFixCoverage?: () => void;
}

export const Overdue = ({ onOpenRequest, onFixCoverage }: Props) => {
  const [reqs, setReqs] = useState<OverdueRequest[]>([]);
  const [gaps, setGaps] = useState<UncoveredCategory[]>([]);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(() => {
    overdue()
      .then((d) => {
        setReqs(d.requests);
        setGaps(d.uncovered.filter((g) => g.supervisors === 0));
      })
      .catch(() => undefined)
      .finally(() => setLoaded(true));
  }, []);

  useEffect(refresh, [refresh]);

  if (!loaded) return <p className="empty">Checking...</p>;

  const clear = reqs.length === 0 && gaps.length === 0;

  return (
    <div className="noticeWrap">
      {clear && (
        <p className="allClear">
          Nothing overdue, and every category with open requests has
          someone covering it.
        </p>
      )}

      {gaps.length > 0 && (
        <section>
          <p className="mono covHead">
            {/* Coverage is tenant-wide, not per-site, so this looks
                identical whichever site is selected. Saying so stops
                it reading as the same alert duplicated. */}
            Categories with no active supervisor, all sites &mdash;{' '}
            {gaps.length}
          </p>
          <ul className="covList">
            {gaps.map((g) => (
              <li
                key={g.id}
                className={onFixCoverage ? 'covMiss rowLink' : 'covMiss'}
                onClick={() => onFixCoverage?.()}
              >
                <span>{g.name}</span>
                <span className="covWhen">
                  {g.open_requests} open &middot;{' '}
                  {g.pending > 0
                    ? `${g.pending} invited, setup not finished`
                    : 'nobody assigned'}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {reqs.length > 0 && (
        <section>
          <p className="mono covHead">
            Past SLA with no response &mdash; {reqs.length}
          </p>
          <ul className="reqList">
            {reqs.map((r) => (
              <li
                key={r.id}
                className={
                  (r.priority === 'urgent' ? 'req urgent' : 'req')
                  + (onOpenRequest ? ' rowLink' : '')
                }
                onClick={() => onOpenRequest?.(r.id)}
              >
                <div className="reqTop">
                  <StatusPill status={r.status as RequestStatus} />
                  <span className="mono">{r.category}</span>
                  <span className="pinUrg">
                    {r.overdueBy}h over {r.slaHours}h
                  </span>
                </div>
                <p className="reqTitle">{r.title}</p>
                <div className="reqMeta">
                  <span className="mono">{r.siteName}</span>
                  <span className="mono">open {r.hoursOpen}h</span>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="noticeRow">
        <button onClick={refresh}>Refresh</button>
        <a className="dlLink" href="/reports/devices.csv" download>
          Device health CSV
        </a>
      </div>
    </div>
  );
};
